import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box, Typography, CircularProgress } from "@mui/material";
import Input from "../../common/InputField";
import { fetchMailDomainStatus } from "../../../store/features/mailDomainAuth";
import {
  updateTicketFromEmail,
  setTicketFromEmailLocal,
  clearTicketFromEmailError,
} from "../../../store/features/ticketFromEmail";
import { updateUser } from "../../../store/features/loginUser";
import "./style.css";

const bodyTextSx = {
  color: "#405261",
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "22px",
};

const headingTextSx = {
  color: "#061522",
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "22px",
  mb: 1.5,
};

function getErrorMessage(payload) {
  if (!payload) return "Something went wrong";
  if (typeof payload === "string") return payload;
  return (
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) && payload.errors[0]?.message) ||
    "Something went wrong"
  );
}

function EmailBranding() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.loginToken);
  const { statusData, statusLoading } = useSelector(
    (state) => state.mailDomainAuth,
  );
  const { loading: saveLoading, error: saveError } = useSelector(
    (state) => state.ticketFromEmail,
  );

  const [emailInput, setEmailInput] = useState("");
  const userTicketEmail = userData?.response?.user?.ticket_from_email;

  const domainVerified = statusData?.mail_domain_verified === true;
  const domainStatusReady = statusData != null;

  useEffect(() => {
    dispatch(fetchMailDomainStatus());
  }, [dispatch]);

  useEffect(() => {
    if (userTicketEmail) {
      dispatch(setTicketFromEmailLocal(userTicketEmail));
      setEmailInput(userTicketEmail);
    }
  }, [dispatch, userTicketEmail]);

  useEffect(() => {
    if (saveError) {
      toast.error(getErrorMessage(saveError));
      dispatch(clearTicketFromEmailError());
    }
  }, [saveError, dispatch]);

  const handleSave = async () => {
    const v = emailInput.trim();
    const action = await dispatch(
      updateTicketFromEmail({ ticket_from_email: v }),
    );
    if (updateTicketFromEmail.fulfilled.match(action)) {
      dispatch(updateUser({ user: { ticket_from_email: v } }));
      setEmailInput(v);
      toast.success("Your ticket sender email saved.");
    }
    // Rejected: slice sets `error`; single toast via saveError effect
  };

  return (
    <div className="profile-main-container">
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          p: { xs: 2, md: 3 },
          gap: { xs: 4, md: 2 },
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: "100%", md: 300 },
          }}
          className="Personal-Info-Text-Container"
        >
          <Typography
            sx={{
              color: "#061522",
              fontFamily: "Inter",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 500,
              lineHeight: "22px",
              mb: 1,
            }}
          >
            My Ticket Email
          </Typography>
          <Typography
            sx={{
              color: "#405261",
              fontFamily: "Inter",
              fontSize: { xs: "13px", md: "14px" },
              fontWeight: 400,
              lineHeight: "22px",
            }}
          >
            After your domain is verified under{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "#061522" }}>
              Custom domain
            </Box>
            , set your personal sender address for ticket messages (for example{" "}
            <Box component="span" sx={{ fontWeight: 500 }}>
              support@yourdomain.com
            </Box>
            ). If you leave this empty, your organization{"'"}s default from{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "#061522" }}>
              Business ticket confirmation
            </Box>{" "}
            is used when one is configured.
          </Typography>
        </Box>

        <Box
          sx={{
            width: { xs: "100%", md: "552px" },
            pr: { md: 7 },
          }}
        >
          {domainStatusReady && !domainVerified && !statusLoading && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: "8px",
                bgcolor: "#FFFBEB",
                border: "1px solid #FCD34D",
              }}
            >
              <Typography
                sx={{
                  ...bodyTextSx,
                  fontSize: 13,
                  color: "#92400E",
                }}
              >
                Verify your domain on the{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Custom domain
                </Box>{" "}
                tab before saving your ticket sender email.
              </Typography>
            </Box>
          )}

          <Typography sx={headingTextSx}>
            Your ticket sender
          </Typography>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            This address is used for outbound ticket email from your account. Use
            a mailbox on the domain you verified (for example{" "}
            <Box component="span" sx={{ fontFamily: "monospace", fontSize: 13 }}>
              ticket+70421@support.yourdomain.com
            </Box>{" "}
            may be used for threading, depending on your provider).
          </Typography>

          <div className="Spacing-Bottom">
            <Input
              title="Your sender email (tickets)"
              placeholder="e.g. support@yourdomain.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              disable={!domainVerified || statusLoading}
            />
          </div>

          <button
            style={{ marginTop: "20px" }}
            className={`Personal-Information-Save-Button${
              saveLoading ? " Personal-Information-Save-Button--primary-loading" : ""
            }`}
            type="button"
            onClick={handleSave}
            disabled={!domainVerified || saveLoading || statusLoading}
          >
            {saveLoading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Save Changes"
            )}
          </button>
        </Box>
      </Box>
    </div>
  );
}

export default EmailBranding;
